// IndexedDB-backed local store — Phase B foundation.
//
// Ported from the old web app's local-store.js. Same shape, typed,
// with a few quality-of-life additions:
//   • Open call times out after 4s so a hung indexedDB.open doesn't
//     freeze every consumer awaiting LS.get/put. On failure we cache
//     the rejection so subsequent calls reject instantly (no repeated
//     4s waits) and surfaces degrade to network-only behavior.
//   • Persistent storage is requested on first import so Android
//     Chrome doesn't evict the cache under storage pressure.
//   • Native SQLite mirror via the Android WebView's `window.VultureStorage`
//     bridge (when available). Every put/delete/clearStore on a MIRRORED
//     store writes through to native SQLite. On openDb(), if any mirrored
//     store is empty in IDB but populated in native SQLite, we hydrate
//     IDB from native BEFORE resolving — so consumers' first getAll
//     returns real data, not an empty page that needs to wait for the
//     network. Native storage lives in the app's private dir and is
//     immune to ordinary WebView cache evictions.
//
// Schema is one object store per data domain plus `pending_writes`
// (Phase C) and `meta` (generic kv). Adding a store: append to
// STORE_SCHEMA and bump DB_VERSION; the onupgradeneeded handler
// creates any missing stores.

import { nativeStore } from "./native-store";

const DB_NAME = "hub-local-v1";
// Bumped to 3 to add pantry_list store. (v2 added recipes_list +
// recipes_get; v1 was the original notes/todos/shopping set.)
// onupgradeneeded only fires on a version change, so without this
// bump existing clients stay on the old schema and pantry RPCs fall
// through to network-only.
const DB_VERSION = 3;

// Stores that get mirrored to native SQLite (durable beyond WebView cache).
// Skipped: `pending_writes` (transient, drained on reconnect), `meta`
// (small kv, regeneratable on demand), `gallery_*` (large/ephemeral image
// metadata — not worth the durability cost).
const MIRRORED_STORES = new Set([
  "notes_files", "notes_dirs", "notes_roots", "notes_templates",
  "todos_lists", "todos_items",
  "shopping_lists", "shopping_items",
  "calendar_events", "calendars", "calendar_members",
  "mealplan_entries",
  "recipes_list", "recipes_get",
  "pantry_list",
]);

interface StoreDef {
  name: string;
  keyPath: string;
  autoIncrement?: boolean;
}

const STORE_SCHEMA: StoreDef[] = [
  { name: "notes_files",      keyPath: "key" },  // key = `${root}:${path}`
  { name: "notes_dirs",       keyPath: "key" },  // key = `${root}:${path}`
  { name: "notes_roots",      keyPath: "key" },  // key = "_"
  { name: "notes_templates",  keyPath: "key" },  // key = root
  { name: "todos_lists",      keyPath: "key" },  // key = "_"
  { name: "todos_items",      keyPath: "key" },  // key = listId
  { name: "shopping_lists",   keyPath: "key" },
  { name: "shopping_items",   keyPath: "key" },  // key = String(listId)
  { name: "calendar_events",  keyPath: "key" },  // key = `${rangeStart}|${rangeEnd}`
  { name: "calendars",        keyPath: "key" },  // key = "_"
  { name: "calendar_members", keyPath: "key" },  // key = calendarId
  { name: "mealplan_entries", keyPath: "key" },  // key = `${from}|${to}`
  { name: "recipes_list",     keyPath: "key" },  // key = "_" (singleton)
  { name: "recipes_get",      keyPath: "key" },  // key = String(recipeId)
  { name: "pantry_list",      keyPath: "key" },  // key = "_" (singleton — per-user pantry, single client = single user)
  { name: "gallery_items",    keyPath: "key" },  // key = `${query||""}|${offset||0}|${limit||100}`
  { name: "gallery_item",     keyPath: "key" },  // key = String(id)
  { name: "pending_writes",   keyPath: "id", autoIncrement: true },
  { name: "meta",             keyPath: "key" },
];

export interface CacheEntry<T = unknown> {
  /** Composite string key. */
  key: string;
  /** Cached RPC response (raw shape). */
  value: T;
  /** Local ms-since-epoch when written. Phase D will surface this in
   *  the "showing cached data from Xm ago" hint. */
  fetchedAt: number;
}

export interface PendingWrite {
  id?: number;
  method: string;
  args: Record<string, unknown>;
  queuedAt: number;
  attempts: number;
  lastErr: string | null;
}

let _dbPromise: Promise<IDBDatabase> | null = null;

// Hydrate empty IDB stores from the native SQLite mirror. Runs once per
// process, on first openDb. Walks `storesWithData()` from the bridge,
// for each mirrored store that's empty in IDB, bulk-copies native rows
// into IDB. Awaiting this in openDb means consumers' first getAll calls
// see real data instead of falling through to "empty cache → wait for
// network" — the whole point of the durability mirror.
async function hydrateFromNative(db: IDBDatabase): Promise<void> {
  if (!nativeStore.available()) return;
  let nativeStores: Set<string>;
  try { nativeStores = await nativeStore.storesWithData(); }
  catch { return; }
  if (nativeStores.size === 0) return;
  for (const storeName of nativeStores) {
    if (!MIRRORED_STORES.has(storeName)) continue;
    if (!db.objectStoreNames.contains(storeName)) continue;
    // Cheap "is IDB store empty" probe — count() with a key range is O(1)
    // on the keys index; we don't need to enumerate rows here.
    let isEmpty = false;
    try {
      const tx = db.transaction(storeName, "readonly");
      const count = await reqPromise<number>(tx.objectStore(storeName).count());
      isEmpty = count === 0;
    } catch { continue; }
    if (!isEmpty) continue;
    // Empty in IDB but native has rows — copy them across. Each native
    // row is {key, value, updatedAt}; CacheEntry shape is {key, value,
    // fetchedAt}. Map updatedAt → fetchedAt so the existing TTL/staleness
    // checks in cache-policy.ts treat the hydrated values consistently.
    let rows: Array<{ key: string; value: unknown; updatedAt: number }>;
    try { rows = await nativeStore.getAll(storeName); }
    catch { continue; }
    if (rows.length === 0) continue;
    try {
      const tx = db.transaction(storeName, "readwrite");
      const os = tx.objectStore(storeName);
      for (const row of rows) {
        os.put({ key: row.key, value: row.value, fetchedAt: row.updatedAt });
      }
      await txPromise(tx);
      console.info(`[local-store] hydrated ${rows.length} rows into IDB.${storeName} from native SQLite`);
    } catch (err: any) {
      console.warn(`[local-store] hydrate ${storeName} failed:`, err?.message || err);
    }
  }
}

function openDb(): Promise<IDBDatabase> {
  if (_dbPromise) return _dbPromise;
  _dbPromise = new Promise((resolve, reject) => {
    let settled = false;
    const timer = setTimeout(() => {
      if (settled) return;
      settled = true;
      reject(new Error("IndexedDB open timed out (4s) — caching disabled"));
    }, 4000);
    let req: IDBOpenDBRequest;
    try { req = indexedDB.open(DB_NAME, DB_VERSION); }
    catch (err) { settled = true; clearTimeout(timer); reject(err); return; }
    req.onupgradeneeded = () => {
      const db = req.result;
      for (const def of STORE_SCHEMA) {
        if (!db.objectStoreNames.contains(def.name)) {
          db.createObjectStore(def.name, {
            keyPath: def.keyPath,
            autoIncrement: !!def.autoIncrement,
          });
        }
      }
    };
    req.onsuccess = () => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      // Hydrate from native SQLite BEFORE resolving — consumers awaiting
      // openDb() get a fully-populated IDB even after a WebView cache
      // wipe. .finally so a hydration failure (e.g. malformed JSON in a
      // native row) still resolves with the empty IDB; degrading to
      // "fetch from network" beats blocking the entire app.
      hydrateFromNative(req.result).finally(() => resolve(req.result));
    };
    req.onerror = () => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      reject(req.error || new Error("IndexedDB open failed"));
    };
    req.onblocked = () => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      reject(new Error("IndexedDB open blocked — close other tabs"));
    };
  });
  // Sticky failure: don't reset _dbPromise so subsequent calls reject
  // instantly instead of paying the 4s timeout every time. A refresh
  // re-runs the module and gets a fresh attempt.
  _dbPromise.catch((err) => {
    console.warn("[local-store] DB open failed, caching disabled for this session:", err.message);
  });
  return _dbPromise;
}

function reqPromise<T>(req: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error || new Error("IndexedDB request failed"));
  });
}
function txPromise(tx: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error || new Error("IndexedDB tx failed"));
    tx.onabort = () => reject(tx.error || new Error("IndexedDB tx aborted"));
  });
}

async function get<T = unknown>(storeName: string, key: string): Promise<CacheEntry<T> | undefined> {
  const db = await openDb();
  const tx = db.transaction(storeName, "readonly");
  return reqPromise<CacheEntry<T> | undefined>(tx.objectStore(storeName).get(key));
}
async function getAll<T = unknown>(storeName: string): Promise<CacheEntry<T>[]> {
  const db = await openDb();
  const tx = db.transaction(storeName, "readonly");
  return reqPromise<CacheEntry<T>[]>(tx.objectStore(storeName).getAll());
}
async function put<T = unknown>(storeName: string, entry: CacheEntry<T>): Promise<void> {
  const db = await openDb();
  const tx = db.transaction(storeName, "readwrite");
  await reqPromise(tx.objectStore(storeName).put(entry));
  await txPromise(tx);
  // Mirror to native SQLite for durability. Fire-and-forget — losing a
  // mirror write isn't catastrophic (next reads still hit IDB until the
  // app is closed), and we'd rather let the user-visible IDB write win
  // the race than block on the JNI hop. native-store internally no-ops
  // when the bridge isn't present (regular web).
  if (MIRRORED_STORES.has(storeName)) {
    nativeStore.set(storeName, String(entry.key), entry).catch(() => {});
  }
}
async function del(storeName: string, key: string): Promise<void> {
  const db = await openDb();
  const tx = db.transaction(storeName, "readwrite");
  await reqPromise(tx.objectStore(storeName).delete(key));
  await txPromise(tx);
  if (MIRRORED_STORES.has(storeName)) {
    nativeStore.delete(storeName, String(key)).catch(() => {});
  }
}
async function clearStore(storeName: string): Promise<void> {
  const db = await openDb();
  const tx = db.transaction(storeName, "readwrite");
  await reqPromise(tx.objectStore(storeName).clear());
  await txPromise(tx);
  if (MIRRORED_STORES.has(storeName)) {
    nativeStore.clearStore(storeName).catch(() => {});
  }
}

// --- pending_writes API (used by Phase C) ---

async function enqueueWrite(method: string, args: Record<string, unknown>): Promise<number> {
  const db = await openDb();
  const tx = db.transaction("pending_writes", "readwrite");
  const result = await reqPromise<IDBValidKey>(tx.objectStore("pending_writes").put({
    method, args,
    queuedAt: Date.now(),
    attempts: 0,
    lastErr: null,
  } as Omit<PendingWrite, "id">));
  await txPromise(tx);
  notifyPendingChange();
  return Number(result);
}
async function listPending(): Promise<PendingWrite[]> {
  const db = await openDb();
  const tx = db.transaction("pending_writes", "readonly");
  return reqPromise<PendingWrite[]>(tx.objectStore("pending_writes").getAll());
}
async function deletePending(id: number): Promise<void> {
  await del("pending_writes", id as unknown as string); // autoIncrement key, not a string
  notifyPendingChange();
}
async function markPendingAttempt(id: number, err: unknown): Promise<void> {
  const db = await openDb();
  const tx = db.transaction("pending_writes", "readwrite");
  const store = tx.objectStore("pending_writes");
  const entry = await reqPromise<PendingWrite | undefined>(store.get(id));
  if (entry) {
    entry.attempts = (entry.attempts || 0) + 1;
    entry.lastErr = err ? String(err).slice(0, 300) : null;
    await reqPromise(store.put(entry));
  }
  await txPromise(tx);
  notifyPendingChange();
}

const PENDING_EVENT = "hub:local-pending-changed";
function notifyPendingChange() {
  try { window.dispatchEvent(new CustomEvent(PENDING_EVENT)); } catch {}
}

// Best-effort: ask the browser not to evict our IndexedDB under storage
// pressure. The most common eviction is Android Chrome wiping site
// data on devices that go a while without opening the PWA — granting
// persistence here prevents it. Fire-and-forget; we don't block boot.
function requestPersistentStorage() {
  if (!navigator.storage || typeof navigator.storage.persist !== "function") return;
  (async () => {
    try {
      const already = (typeof navigator.storage.persisted === "function")
        ? await navigator.storage.persisted()
        : false;
      if (already) return;
      const granted = await navigator.storage.persist();
      console.info("[local-store] storage.persist():", granted ? "granted" : "denied");
    } catch (err: any) {
      console.warn("[local-store] storage.persist() failed:", err?.message || err);
    }
  })();
}
requestPersistentStorage();

export const localStore = {
  ready: openDb,
  get,
  getAll,
  put,
  delete: del,
  clearStore,
  enqueueWrite,
  listPending,
  deletePending,
  markPendingAttempt,
  PENDING_EVENT,
};
